import type { z } from 'zod';
import { Check, LinkIcon, X } from 'lucide-react';
import { capitalize } from 'string-ts';

import type { SocialPage } from '@ballerine/common';

import { ctw } from '@/common';
import {
  AdsProviders,
  socialMediaNameMap,
} from '@/components/templates/digital-footprint/constants';
import { FacebookIcon } from './icons/FacebookIcon';
import { InstagramIcon } from './icons/InstagramIcon';
import { LinkedInIcon } from './icons/LinkedInIcon';
import { TiktokIcon } from './icons/TiktokIcon';
import { XIcon } from './icons/TwitterIcon';

const socialMediaIcons = {
  facebook: <FacebookIcon className="h-6 w-6" />,
  instagram: <InstagramIcon className="h-6 w-6" />,
  linkedin: <LinkedInIcon className="mx-[2px] h-5 w-5" />,
  tiktok: <TiktokIcon className="h-6 w-6" />,
  twitter: <XIcon className="mx-[2px] h-5 w-5" />,
} as const;

type SocialMediaObject = z.infer<typeof SocialPage> | { url: string | null } | null;

export const DigitalFootprintSocialMedia = (pages: {
  facebook: SocialMediaObject;
  instagram: SocialMediaObject;
  twitter: SocialMediaObject;
  linkedin: SocialMediaObject;
  tiktok: SocialMediaObject;
}) => {
  return (
    <div className="space-y-4">
      {AdsProviders.map(provider => {
        const page = pages[provider];
        const title = socialMediaNameMap[provider];
        const isDetected = !!page?.url;

        return (
          <div
            key={`${provider}-${page?.url}-${title}`}
            className="grid grid-cols-[minmax(200px,auto)_minmax(200px,1fr)]"
          >
            <div key={title} className="flex items-center gap-3 py-1">
              <div className="flex-shrink-0">
                {isDetected ? (
                  <div className="flex h-5 w-5 items-center justify-center rounded-full bg-green-100">
                    <Check className="h-2.5 w-2.5 text-green-500" strokeWidth={6} />
                  </div>
                ) : (
                  <div className="flex h-5 w-5 items-center justify-center rounded-full bg-red-100">
                    <X className="h-2.5 w-2.5 text-red-500" strokeWidth={6} />
                  </div>
                )}
              </div>

              <div className={ctw('flex items-center gap-2', !isDetected && 'opacity-20')}>
                {socialMediaIcons[provider]}
                <span className="font-medium text-gray-900">{title}</span>
              </div>
            </div>

            {!!page && page.url && (
              <div className="ml-2 flex items-center gap-1 text-sm">
                <LinkIcon className="h-4 w-4 text-gray-400" />
                <a
                  href={page.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800"
                  title={`Visit ${capitalize(provider)} page`}
                >
                  {page.url}
                </a>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
